import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import MojitoCore from 'mojito/core';
import BetsTypes from 'services/bets/types.js';
import BetsUtils from 'services/bets/utils';
import MatchAccaUtils from './utils';
import serviceFactory from './service/service-factory';
import { selectOddsFormat } from 'services/user-settings/selectors';
import EventTypes from 'services/sports-content/events/types.js';
import { actions as eventActions } from 'services/sports-content/events/slice.js';
import EventUtils from 'services/sports-content/events/utils.js';
import { selectMarkets } from 'services/sports-content/events/selectors';
import { actions as betslipActions } from 'services/betslip/slice.js';
import betslipModelFactory from 'services/betslip/model-factory.js';
import { first, pick } from 'mojito/utils';
import { selectMatchAccaById, selectMatchAcca } from './selectors';

const { MATCH_ACCA } = BetsTypes.LEG_SORT;
const reduxInstance = MojitoCore.Services.redux;
const SUCCESS_STATUS = 'SUCCESS';

/**
 * The name of the match acca store. Will be used to register in global redux store.
 *
 * @constant
 * @type {string}
 * @memberof Mojito.Services.MatchAcca
 */
export const STORE_KEY = 'matchAccaStore';

/**
 * Defines the state of MatchAccaStore.
 *
 * @typedef MatchAccaState
 *
 * @property {object} matchAccaPrototype - Default match acca object.
 * @property {object} matchAccas - All match accas objects.
 * @property {object} matchAccaBetsOdds - Match acca bets odds object. Key is a bet id and value is an object with leg ids as keys and odds as values.
 * This object contains odds for already placed/saved bet legs which are not part of the current match acca and appears here as part of odds validation process during manipulations with saved bets.
 *
 * @memberof Mojito.Services.MatchAcca
 */
export const initialState = {
    matchAccaPrototype: MatchAccaUtils.createMatchAcca(),
    matchAccas: {},
    matchAccaBetsOdds: {},
};

export const { reducer, actions } = createSlice({
    name: STORE_KEY,
    initialState,
    reducers: {
        updateMatchAcca(state, { payload }) {
            const { eventId, options } = payload;
            state.matchAccas[eventId] = {
                ...state.matchAccas[eventId],
                ...options,
            };
        },
        addSelection(state, { payload }) {
            const { selection, parent } = payload;
            const { marketId, eventId } = parent;
            const { id: selectionId } = selection;
            if (!MatchAccaUtils.hasMatchAcca(state.matchAccas, eventId)) {
                // reset matchAcca
                state.matchAccas[eventId] = {
                    ...state.matchAccas[eventId],
                    ...MatchAccaUtils.createMatchAcca(),
                };
            }

            const matchAcca = state.matchAccas[eventId];
            if (!MatchAccaUtils.hasSelection(matchAcca, selectionId)) {
                const { selections: matchAccaSelections } = matchAcca;
                const existingSelections = MatchAccaUtils.removeSelectionByMarketId(
                    matchAccaSelections,
                    marketId
                );
                const selections = [...existingSelections, selection];
                state.matchAccas[eventId] = { ...state.matchAccas[eventId], selections };
            }
        },

        removeSelection(state, { payload }) {
            const { parent, selectionId } = payload;
            const { eventId } = parent;
            const matchAcca = state.matchAccas[eventId];
            if (MatchAccaUtils.hasSelection(matchAcca, selectionId)) {
                const { selections } = matchAcca;
                const remainingSelections = MatchAccaUtils.removeSelectionById(
                    selections,
                    selectionId
                );

                state.matchAccas[eventId] = {
                    ...state.matchAccas[eventId],
                    selections: remainingSelections,
                    odds: undefined,
                    hasInvalidCombinations: false,
                    hasError: false,
                };
            }
        },

        calculateOddsSuccess(state, { payload }) {
            const { eventId, oddsLines } = payload;
            const { odds: receivedOdds, exactOdds } = first(oddsLines) || {};
            const odds = receivedOdds || EventTypes.UNAVAILABLE_ODDS;

            const hasInvalidCombinations =
                parseFloat(odds) === parseFloat(EventTypes.UNAVAILABLE_ODDS);

            state.matchAccas[eventId] = {
                ...state.matchAccas[eventId],
                odds: { odds, exactOdds },
                hasInvalidCombinations,
                hasError: false,
            };
        },

        calculateOddsFailed(state, { payload: eventId }) {
            state.matchAccas[eventId] = {
                ...state.matchAccas[eventId],
                odds: undefined,
                hasInvalidCombinations: false,
                hasError: true,
            };
        },
        calculateOddsForLegSuccess(state, { payload }) {
            const { oddsLines, betId, legId } = payload;
            const { odds } = first(oddsLines);
            state.matchAccaBetsOdds[betId] = {
                ...state.matchAccaBetsOdds[betId],
                [legId]: odds || EventTypes.UNAVAILABLE_ODDS,
            };
        },
        calculateOddsForLegFailed(state, { payload }) {
            const { betId, legId } = payload;
            state.matchAccaBetsOdds[betId] = {
                ...state.matchAccaBetsOdds[betId],
                [legId]: EventTypes.UNAVAILABLE_ODDS,
            };
        },
        clear(state, { payload: eventId }) {
            delete state.matchAccas[eventId];
        },

        reset() {
            return { ...initialState };
        },
    },
    extraReducers: builder => {
        builder
            .addCase(
                betslipActions.addCompositeSuccess,
                (state, { payload: { requestPayload = {} } }) => {
                    const parent = requestPayload.parent || {};
                    const { legSort } = requestPayload;
                    const { eventId } = parent;

                    if (
                        legSort === MATCH_ACCA &&
                        MatchAccaUtils.hasMatchAcca(state.matchAccas, eventId)
                    ) {
                        delete state.matchAccas[eventId];
                    }
                }
            )
            .addCase(eventActions.updateEvent, (state, { payload }) => {
                const { eventId, event } = payload;
                const matchAcca = state.matchAccas[eventId];

                if (matchAcca && !event) {
                    delete state.matchAccas[eventId];
                }
            })
            .addCase(eventActions.updateMarket, (state, { payload }) => {
                const { eventId, marketId, market } = payload;
                const matchAcca = state.matchAccas[eventId];
                if (matchAcca) {
                    state.matchAccas[eventId] = MatchAccaUtils.syncSelections(
                        matchAcca,
                        marketId,
                        market?.selections
                    );
                }
            });
    },
});

/**
 * Calculate odds.
 *
 * @function calculateOdds
 *
 * @param {string} eventId - Event id.
 * @returns {Mojito.Core.Services.redux.ThunkFunction} Calculate odds thunk. Dispatches calculateOddsSuccess or calculateOddsFailed when calculateOdds process is finalised.
 * @memberof Mojito.Services.MatchAcca.actions
 */
actions.calculateOdds = eventId => (dispatch, getState) => {
    const state = getState()[STORE_KEY];
    const matchAcca = state.matchAccas[eventId];

    if (!matchAcca) {
        return;
    }
    const { selections } = matchAcca;
    // If there are less than 2 selections in match acca - do not calculate odds as
    // this type of match acca is not complete and can't be placed.
    if (selections.length < 2) {
        return;
    }
    const oddsFormat = selectOddsFormat(getState());
    serviceFactory
        .getService()
        .calculateOdds(selections, MATCH_ACCA, oddsFormat)
        .then(res => {
            if (res.status === SUCCESS_STATUS) {
                dispatch(actions.calculateOddsSuccess({ oddsLines: res.data.oddsLines, eventId }));
            } else {
                dispatch(actions.calculateOddsFailed(eventId));
            }
        })
        .catch(() => dispatch(actions.calculateOddsFailed(eventId)));
};

/**
 * Calculate odds for already placed/saved bet legs.
 *
 * @function calculateOddsForLegs
 *
 * @param {Array<Mojito.Services.Bets.types.Leg>} legs - Bet legs list.
 * @param {string} betId - Bet id.
 * @returns {Mojito.Core.Services.redux.ThunkFunction} Calculate odds thunk. Dispatches calculateOddsForLegSuccess or calculateOddsForLegFailed when calculateOdds process is finalised.
 * @memberof Mojito.Services.MatchAcca.actions
 */
actions.calculateOddsForLegs = (legs, betId) => {
    return (dispatch, getState) => {
        const state = getState();
        const oddsFormat = selectOddsFormat(state);
        legs.forEach(leg => {
            const marketIds = BetsUtils.getLegMarketIds(leg);
            const markets = Object.values(pick(selectMarkets(state), ...marketIds));
            const selectionIds = BetsUtils.getLegSelectionIds(leg);
            const selections = EventUtils.getSelectionsFromMarkets(markets, selectionIds);
            const selectionParts = selections.map(selection => {
                const market = markets.find(
                    market => !!EventUtils.getSelectionFromMarket(market, selection.id)
                );
                const selectionExt = { ...selection, marketId: market?.id };
                return betslipModelFactory.getSelection(selectionExt);
            });
            const legId = BetsUtils.getLegId(leg);
            if (selectionParts.length) {
                serviceFactory
                    .getService()
                    .calculateOdds(selectionParts, MATCH_ACCA, oddsFormat)
                    .then(res => {
                        const { status, data } = res;
                        if (status === SUCCESS_STATUS) {
                            dispatch(
                                actions.calculateOddsForLegSuccess({
                                    oddsLines: data.oddsLines,
                                    betId,
                                    legId,
                                })
                            );
                        } else {
                            dispatch(actions.calculateOddsForLegFailed({ betId, legId }));
                        }
                    })
                    .catch(() => dispatch(actions.calculateOddsForLegFailed({ betId, legId })));
            }
        });
    };
};

reduxInstance.actionListener.startListening({
    matcher: isAnyOf(
        eventActions.updateEvent,
        eventActions.updateMarket,
        actions.addSelection,
        actions.removeSelection
    ),
    effect: (action, listenerApi) => {
        const { dispatch, getState, getOriginalState } = listenerApi;
        const { payload } = action;
        const eventId = payload.eventId || payload.parent?.eventId;
        const hasMatchAcca = MatchAccaUtils.hasMatchAcca(selectMatchAcca(getState()), eventId);
        if (!hasMatchAcca) {
            return;
        }
        const matchAcca = selectMatchAccaById(eventId, getState());
        const prevMatchAcca = selectMatchAccaById(eventId, getOriginalState());
        const isEventUpdate = action.type === eventActions.updateEvent.toString();
        // Once event updated always recalculate odds.
        if (MatchAccaUtils.hasChanges(matchAcca, prevMatchAcca) || isEventUpdate) {
            dispatch(actions.calculateOdds(eventId));
        }
    },
});

/**
 * Config object used to initialise match acca service layer.
 *
 * @typedef MatchAccaConfig
 * @type {object}
 * @property {Mojito.Services.Betslip.AbstractBetslipService} service - Match acca service implementation.
 * @property {string} serviceUrl - URL that will be used by <code>service<code/> instance.
 *
 * @memberof Mojito.Services.MatchAcca
 */

/**
 * Actions related to the MatchAcca services.
 *
 * @class MatchAccaActions
 * @name actions
 * @memberof Mojito.Services.MatchAcca
 */

/**
 * Add Selection.
 *
 * @function addSelection
 * @type {Mojito.Core.Services.redux.ActionCreator}
 * @param {{selection: object, parent: object}} payload - Payload contains selection object and selection parent info.
 *
 * @memberof Mojito.Services.MatchAcca.actions
 */

/**
 * Remove Selection.
 *
 * @function removeSelection
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @param {{selectionId: string, parent: object}} payload - Payload contains selection id and selection parent info.
 *
 * @memberof Mojito.Services.MatchAcca.actions
 */

/**
 * Calculate odds success action.
 *
 * @function calculateOddsSuccess
 * @type {Mojito.Core.Services.redux.ActionCreator}
 * @param {{eventId: string, oddsLines: object}} payload - Payload contains event id and odds lines object.
 *
 * @memberof Mojito.Services.MatchAcca.actions
 */

/**
 * Calculate odds failed action.
 *
 * @function calculateOddsFailed
 * @type {Mojito.Core.Services.redux.ActionCreator}
 * @param {string} eventId - Event id.
 *
 * @memberof Mojito.Services.MatchAcca.actions
 */

/**
 * Calculate odds for leg success action.
 *
 * @function calculateOddsForLegSuccess
 * @type {Mojito.Core.Services.redux.ActionCreator}
 * @param {{betId: string, legId: string, oddsLines: object}} payload - Payload contains bet id, leg id and odds lines object.
 *
 * @memberof Mojito.Services.MatchAcca.actions
 */

/**
 * Calculate odds for leg failed action.
 *
 * @function calculateOddsForLegFailed
 * @type {Mojito.Core.Services.redux.ActionCreator}
 * @param {{betId: string, legId: string}} payload - Payload contains bet id and leg id.
 *
 * @memberof Mojito.Services.MatchAcca.actions
 */

/**
 * Remove match acca by event id.
 *
 * @function clear
 * @type {Mojito.Core.Services.redux.ActionCreator}
 * @param {string} eventId - Event id of the match acca that should be removed.
 *
 * @memberof Mojito.Services.MatchAcca.actions
 */

/**
 * Reset match acca state.
 *
 * @function reset
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @memberof Mojito.Services.MatchAcca.actions
 */

reduxInstance.injectReducer(STORE_KEY, reducer);
